import { useEffect, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { Dashboard } from "./components/Dashboard";
import { ProjectsPage } from "./pages/ProjectsPage";
import Surveys from "./components/SurveyView/SurveysPage";
import "./App.css";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import SurveyDashboard from "./components/SurveyView/SurveyDashboard/SurveyDashboard";
import { useGetTokenFromParams } from "./hooks/useGetTokenFromParams";
import { useDispatch, useSelector } from "react-redux";
import { Login } from "./components/Login";
import NotFound from "./pages/NotFound/NotFound";
import { useGetFlagsQuery, useValidateTokenQuery } from "./store/api/lumi/auth";
import { setFlags } from "./store/slices/authSlice";
import { FiltersPanel, HeaderPanel, WidgetPanel, TabPanel, TitleEdit} from "./components/NewUI";

const antIcon = (
    <LoadingOutlined
        style={{
            fontSize: 24,
        }}
        spin
    />
);

const App = () => {
    const [profile, setProfile] = useState({ username: "unknown" });
    const { isTokenExist, tokenFromQuery } = useGetTokenFromParams();
    const isLoggedIn = localStorage.getItem("loggedIn") === "true";
    const token = isTokenExist
        ? tokenFromQuery
        : localStorage.getItem("lumitoken");

    const dispatch = useDispatch();
    const { data: flags, isSuccess } = useGetFlagsQuery(  undefined, {
        skip:  !isLoggedIn
    });
    useEffect(() => {
        if (isSuccess && flags) {
            dispatch(setFlags(flags));
        }
    }, [isSuccess]);
    const loginCallback = (response_data) => {
        localStorage.setItem("lumitoken", response_data["token"]);
        localStorage.setItem("loggedIn", true);
    };


    const PrivateRoute = ({ children }) => {
        const navigate = useNavigate();
        const location = useLocation();
        const isLoggedIn = localStorage.getItem("loggedIn") === "true";
        const { tokenFromQuery } = useGetTokenFromParams();
        const storedToken = localStorage.getItem("lumitoken");
        const { data, isFetching, isError } = useValidateTokenQuery(undefined, {
            skip: !tokenFromQuery || isLoggedIn
        });

        useEffect(() => {
            if (isLoggedIn && tokenFromQuery){
                if (tokenFromQuery && tokenFromQuery !== storedToken) {
                    localStorage.setItem("lumitoken", tokenFromQuery);
                    localStorage.setItem("loggedIn", "true");
                    navigate(location.pathname, { replace: true });
                    window.location.reload();
                }
                navigate(location.pathname, { replace: true });
                window.location.reload();
            }
            if (!isLoggedIn && !isFetching) {
                if (isError || !data?.isValid) {
                    navigate("/login", { replace: true });
                } else {
                    localStorage.setItem("lumitoken", tokenFromQuery);
                    localStorage.setItem("loggedIn", true);
                    navigate(location.pathname, { replace: true });
                }
            }
        }, [isLoggedIn, tokenFromQuery, data, isFetching, isError, navigate, location.pathname]);

        return isLoggedIn ? children : null;

    };

    const PublicRoute = ({ children }) => {
        const isLoggedIn = localStorage.getItem("loggedIn");
        return isLoggedIn ? <Navigate to="/projects" replace /> : children;
    };

    return (
        <div className="app-container">
            <DndProvider backend={HTML5Backend}>
                <Router basename={process.env.PUBLIC_URL}>
                    <Routes>
                        <Route
                            element={
                                <PublicRoute
                                    element={
                                        <Login loginCallback={loginCallback} />
                                    }
                                    route="/login"
                                />
                            }
                        />
                        <Route
                            path="/"
                            element={
                                token ? (
                                    <Navigate to="/projects" replace />
                                ) : (
                                    <Navigate to="/login" replace />
                                )
                            }
                        />
                        <Route
                            path="/login"
                            element={
                                <PublicRoute>
                                    <Login loginCallback={loginCallback} />
                                </PublicRoute>
                            }
                        />
                        <Route
                            path="/projects"
                            element={
                                <PrivateRoute>
                                    <ProjectsPage profile={profile} />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/projects/:id/dashboard"
                            element={
                                <PrivateRoute>
                                    <Dashboard />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/projects/:id/dashboard/:dashboardName"
                            element={
                                <PrivateRoute>
                                    <Dashboard />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/surveys"
                            element={
                                <PrivateRoute>
                                    <Surveys profile={profile} />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/surveys/:id/dashboard"
                            element={
                                <PrivateRoute>
                                    <SurveyDashboard profile={profile} />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/title-edit"
                            element={<TitleEdit />}
                        />
                        <Route
                            path="/filters-panel"
                            element={<FiltersPanel />}
                        />
                        <Route
                            path="/header-ui"
                            element={<HeaderPanel />}
                        />
                        <Route
                            path="/widget-panel"
                            element={<WidgetPanel />}
                        />
                        <Route
                            path="/tab"
                            element={<TabPanel />}
                        />
                        <Route
                            path="/title-edit"
                            element={<TitleEdit />}
                        />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Router>
            </DndProvider>
        </div>
    );
};

export default App;
